// views/Dashboard/PainelExecutivo/sections/equipes/EquipesTela.tsx
//
// Só a interface da área "Equipes": recebe tudo pronto por props e não fala com
// o banco nem com o login. A ligação com os dados fica em `AreaEquipes.tsx` —
// separado assim, a tela abre numa pré-visualização fora do login.
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download, Info, Loader2, RefreshCw, Users } from 'lucide-react';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';
import InfoTip from '../../../../../src/components/InfoTip';
import {
  UNIDADES,
  formatarIndicador,
  valorDaEquipe,
  valorDoIndicador,
  type EquipeBi,
  type Indicador,
  type Unidade,
  type UnidadeBi,
} from '../../../../../src/lib/dashboard/eficienciaEquipes';
import { OPCOES_PERIODO_EMISSAO, type PeriodoEmissao } from '../../../DashboardFaturamento/lib/periodoEmissao';

export type FiltroUnidade = Unidade | 'todas';

interface Props {
  unidades: UnidadeBi[];
  filtroUnidade: FiltroUnidade;
  onFiltroUnidade: (u: FiltroUnidade) => void;
  periodo: PeriodoEmissao;
  onPeriodo: (p: PeriodoEmissao) => void;
  loading: boolean;
  erro: string | null;
  /** As emissões (prazo do Faturamento) chegam depois do resto. */
  carregandoPrazo: boolean;
  erroPrazo: string | null;
  ultimaAtualizacao: Date | null;
  onAtualizar: () => void;
  onExportar: () => void;
  exportando: boolean;
}

/** Colunas que dependem das emissões — mostram "…" enquanto elas carregam. */
const DEPENDEM_DO_PRAZO = new Set(['pct_fora_prazo', 'tempo_emissao']);

const selectCls =
  'px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ' +
  'text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 ' +
  'dark:[color-scheme:dark]';

const FILTROS: { valor: FiltroUnidade; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  ...UNIDADES.map((u) => ({ valor: u, rotulo: u })),
];

const plural = (n: number, um: string, varios: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

export const TabelaEquipe: React.FC<{ equipe: EquipeBi; carregandoPrazo: boolean }> = ({ equipe, carregandoPrazo }) => {
  // Sem ordem escolhida, a tabela vai por nome: o painel não abre como ranking.
  const [ordem, setOrdem] = useState<{ id: string; desc: boolean } | null>(null);

  const linhas = useMemo(() => {
    if (!ordem) return equipe.pessoas;
    const col = equipe.colunas.find((c) => c.id === ordem.id);
    if (!col) return equipe.pessoas;
    return equipe.pessoas.slice().sort((a, b) => {
      const va = valorDoIndicador(col, a.valores);
      const vb = valorDoIndicador(col, b.valores);
      // Quem não tem valor vai para o fim, nos dois sentidos.
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return ordem.desc ? vb - va : va - vb;
    });
  }, [equipe, ordem]);

  const valoresDaEquipe = useMemo(() => equipe.pessoas.map((l) => l.valores), [equipe]);

  const alternarOrdem = (id: string) =>
    setOrdem((atual) => (atual?.id === id ? (atual.desc ? { id, desc: false } : null) : { id, desc: true }));

  const celula = (c: Indicador, valor: number | null) =>
    carregandoPrazo && DEPENDEM_DO_PRAZO.has(c.id)
      ? <span className="text-gray-300 dark:text-gray-600" title="Carregando as emissões de boleto">…</span>
      : formatarIndicador(valor, c.formato);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="px-4 pt-4 pb-2 flex items-baseline gap-2">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{equipe.departamento}</h3>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">{plural(equipe.pessoas.length, 'pessoa', 'pessoas')}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th scope="col" className="sticky left-0 z-10 bg-white dark:bg-gray-800 text-left py-2 pl-4 pr-3 font-semibold align-bottom">
                Pessoa
              </th>
              {equipe.colunas.map((c) => {
                const ativa = ordem?.id === c.id;
                return (
                  // Título quebra em até duas linhas: numa linha só, as equipes com
                  // muitas colunas (Financeiro, Faturamento) estouravam a largura.
                  <th key={c.id} scope="col" className="py-2 px-2.5 text-right font-semibold align-bottom">
                    <span className="inline-flex items-end justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => alternarOrdem(c.id)}
                        className={`max-w-[5.5rem] text-right leading-tight uppercase tracking-wide hover:text-gray-800 dark:hover:text-gray-200 ${ativa ? 'text-primary' : ''}`}
                        aria-label={`Ordenar por ${c.rotulo}`}
                      >
                        {c.rotulo}
                        {ativa && (ordem!.desc
                          ? <ArrowDown size={11} className="inline ml-0.5 -mt-0.5" />
                          : <ArrowUp size={11} className="inline ml-0.5 -mt-0.5" />)}
                      </button>
                      <InfoTip label={`O que é ${c.rotulo}`}>{c.explicacao}</InfoTip>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {linhas.map((l) => (
              <tr key={l.pessoa.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 py-2.5 pl-4 pr-3">
                  {/* No celular o nome quebra: inteiro numa linha, a coluna fixa
                      ocupava a tela e não sobrava espaço para os números. */}
                  <div className="font-medium text-gray-900 dark:text-white max-w-[8.5rem] sm:max-w-none sm:whitespace-nowrap">{l.pessoa.nome}</div>
                  {l.semIndicadorMedido && (
                    <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 max-w-[8.5rem] sm:max-w-none sm:whitespace-nowrap">
                      Nada medido desta função no período
                      <InfoTip label="Por que nada foi medido" tone="warning">
                        A pessoa entrou no sistema, mas nenhum indicador desta função registrou trabalho.
                        Em geral é trabalho que o sistema ainda não mede (rateio, planilhas, atendimento
                        pelo celular) — não quer dizer que ela ficou parada.
                      </InfoTip>
                    </div>
                  )}
                </td>
                {equipe.colunas.map((c) => (
                  <td key={c.id} className="py-2.5 px-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {celula(c, valorDoIndicador(c, l.valores))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          {equipe.pessoas.length > 1 && (
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900 py-2.5 pl-4 pr-3 text-gray-900 dark:text-white rounded-bl-2xl">
                  Equipe
                </td>
                {equipe.colunas.map((c) => (
                  <td key={c.id} className="py-2.5 px-2.5 text-right tabular-nums text-gray-900 dark:text-white whitespace-nowrap">
                    {celula(c, valorDaEquipe(c, valoresDaEquipe))}
                    {c.calculo.tipo === 'bruto' && c.calculo.equipe === 'media' && (
                      <span className="ml-1 text-[10px] font-normal text-gray-400 dark:text-gray-500">média</span>
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

const EquipesTela: React.FC<Props> = ({
  unidades,
  filtroUnidade,
  onFiltroUnidade,
  periodo,
  onPeriodo,
  loading,
  erro,
  carregandoPrazo,
  erroPrazo,
  ultimaAtualizacao,
  onAtualizar,
  onExportar,
  exportando,
}) => {
  const visiveis = filtroUnidade === 'todas' ? unidades : unidades.filter((u) => u.unidade === filtroUnidade);
  const ninguem = unidades.every((u) => u.totalPessoas === 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="bi-equipes-periodo" className="sr-only">Período</label>
          <select
            id="bi-equipes-periodo"
            className={selectCls}
            value={periodo}
            onChange={(e) => onPeriodo(e.target.value as PeriodoEmissao)}
          >
            {OPCOES_PERIODO_EMISSAO.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <div role="group" aria-label="Unidade" className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-800">
            {FILTROS.map((f) => (
              <button
                key={f.valor}
                type="button"
                onClick={() => onFiltroUnidade(f.valor)}
                aria-pressed={filtroUnidade === f.valor}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  filtroUnidade === f.valor
                    ? 'bg-primary text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                {f.rotulo}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={onAtualizar}
            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Atualizar"
            aria-label="Atualizar os indicadores das equipes"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>

          {ultimaAtualizacao && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">
              atualizado às {ultimaAtualizacao.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onExportar}
          disabled={exportando || loading || carregandoPrazo || !!erro}
          title={carregandoPrazo ? 'Aguarde o prazo do Faturamento carregar' : undefined}
          className="lg:ml-auto w-full lg:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
        >
          {exportando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {exportando ? 'Exportando...' : 'Exportar Equipes'}
        </button>
      </div>

      {erro ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 px-4 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
          <p className="text-sm font-semibold text-red-600 dark:text-red-400 max-w-md">{erro}</p>
          <button onClick={onAtualizar} className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors">
            Tentar novamente
          </button>
        </div>
      ) : loading && ninguem ? (
        <div className="py-12"><LoadingSpinner label="Carregando indicadores das equipes..." size="sm" /></div>
      ) : ninguem ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 px-4 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
          <Users size={22} className="text-gray-300 dark:text-gray-600" />
          <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Ninguém com unidade no cadastro</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm">
            Defina a unidade (Goiânia ou Morrinhos) de cada pessoa em Configurações › Usuários.
          </p>
        </div>
      ) : (
        <>
          {erroPrazo && (
            <p className="text-xs text-amber-600 dark:text-amber-400">{erroPrazo} As demais colunas estão corretas.</p>
          )}

          {visiveis.map((u) => (
            <section key={u.unidade} className="space-y-3" aria-label={u.unidade}>
              <div className="flex items-baseline gap-2">
                <h2 className="text-base font-bold text-gray-800 dark:text-white">{u.unidade}</h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {plural(u.totalPessoas, 'pessoa', 'pessoas')} · {plural(u.equipes.length, 'equipe', 'equipes')}
                </span>
              </div>
              {u.equipes.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Ninguém com a unidade {u.unidade} no cadastro.</p>
              ) : (
                u.equipes.map((e) => <TabelaEquipe key={e.departamento} equipe={e} carregandoPrazo={carregandoPrazo} />)
              )}
            </section>
          ))}

          <div className="flex items-start gap-2 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-[11px] text-gray-500 dark:text-gray-400">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p>
                Os números contam o que cada pessoa registrou no sistema no período. Trabalho feito fora
                dele — conversa no celular, planilha, rateio — não aparece.
              </p>
              <p>
                Conta entrega, não clique: a mesma fatura calculada duas vezes conta uma, e negociação
                marcada como perdida não conta como trabalhada. Compare pessoas da mesma equipe — cada
                função tem a sua régua.
              </p>
              <p>
                Entram as pessoas ativas com unidade no cadastro (Configurações › Usuários); a equipe é o
                departamento do perfil. Diretoria e contas genéricas ficam de fora. Só administradores veem
                esta área.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EquipesTela;
